import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Participant } from '../types';

/**
 * User Context
 * Manages the current user/participant state
 */

interface UserState {
  currentParticipant: Participant | null;
  isAdmin: boolean;
  eventId: string | null;
}

interface UserContextValue extends UserState {
  setCurrentParticipant: (participant: Participant | null) => void;
  setIsAdmin: (isAdmin: boolean) => void;
  setEventId: (eventId: string | null) => void;
  clearUser: () => void;
}

const UserContext = createContext<UserContextValue | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [state, setState] = useState<UserState>({
    currentParticipant: null,
    isAdmin: false,
    eventId: null,
  });

  const setCurrentParticipant = useCallback((participant: Participant | null) => {
    setState((prev) => ({
      ...prev,
      currentParticipant: participant,
    }));
  }, []);

  const setIsAdmin = useCallback((isAdmin: boolean) => {
    setState((prev) => ({
      ...prev,
      isAdmin,
    }));
  }, []);

  const setEventId = useCallback((eventId: string | null) => {
    setState((prev) => ({
      ...prev,
      eventId,
    }));
  }, []);

  const clearUser = useCallback(() => {
    setState({
      currentParticipant: null,
      isAdmin: false,
      eventId: null,
    });
  }, []);

  const value: UserContextValue = {
    ...state,
    setCurrentParticipant,
    setIsAdmin,
    setEventId,
    clearUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}


